import { useId } from 'react'
import PropTypes from 'prop-types'

const plain = n => String(n)

/**
 * A labelled range slider on native `input type="range"` elements, so the
 * keyboard (arrows, Page Up/Down, Home/End), screen readers and touch all
 * work. `value` as a number gives one handle (an upper bound); as
 * `{ min, max }` it gives two overlaid handles on one track. The label sits
 * above with the live value at the right ("$800 – $2,500 / mo"); `openTop`
 * prints the top of the scale as "5,000+" and means no upper limit.
 * `format` prints a number, `unit` follows the value text.
 */
export default function RangeSlider({
  label,
  min,
  max,
  step = 1,
  value,
  onChange,
  format = plain,
  unit,
  openTop = false,
  minLabel,
  maxLabel,
  className = '',
  labelClassName = 'font-sans text-sm font-medium text-ink-soft',
  id: idProp,
}) {
  const autoId = useId()
  const id = idProp || autoId
  const labelId = `${id}-label`
  const dual = typeof value === 'object' && value !== null
  const lo = dual ? value.min : min
  const hi = dual ? value.max : value
  const span = max - min || 1
  const pct = v => ((Math.min(Math.max(v, min), max) - min) / span) * 100

  const print = v => `${format(v)}${openTop && v >= max ? '+' : ''}`
  const spoken = v =>
    `${format(v)}${openTop && v >= max ? ' or more' : ''}${unit ? ` ${unit}` : ''}`
  const shown = dual ? `${print(lo)} – ${print(hi)}` : print(hi)

  const setMin = v => onChange({ min: Math.min(v, hi), max: hi })
  const setMax = v =>
    dual ? onChange({ min: lo, max: Math.max(v, lo) }) : onChange(v)

  // With both handles stacked, the one that can still move goes on top:
  // the lower handle once it sits in the upper half of the track.
  const minOnTop = dual && lo > min + span / 2

  return (
    <div
      className={`min-w-0 ${className}`}
      role={dual ? 'group' : undefined}
      aria-labelledby={dual ? labelId : undefined}
    >
      <div className="flex items-baseline justify-between gap-4">
        {dual ? (
          <span id={labelId} className={labelClassName}>
            {label}
          </span>
        ) : (
          <label id={labelId} htmlFor={`${id}-max`} className={labelClassName}>
            {label}
          </label>
        )}
        <span
          className="font-sans font-semibold text-ink tabular whitespace-nowrap"
          data-testid="range-value"
        >
          {shown}
          {unit && (
            <span className="font-normal text-sm text-ink-mute"> {unit}</span>
          )}
        </span>
      </div>
      <div className="range-slider relative h-11">
        {/* The thumb's centre travels from 12px in at each end (half the
            24px thumb), so the track and the selected span sit inset by
            the same amount and line up with the thumbs. */}
        <div
          aria-hidden="true"
          className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-1"
        >
          <div className="absolute inset-0 rounded-full bg-ink-line" />
          <div
            className="absolute inset-y-0 rounded-full bg-cover"
            style={{ left: `${pct(lo)}%`, right: `${100 - pct(hi)}%` }}
          />
        </div>
        {dual && (
          <input
            type="range"
            id={`${id}-min`}
            className={`range-dual ${minOnTop ? 'z-[2]' : 'z-[1]'}`}
            min={min}
            max={max}
            step={step}
            value={lo}
            aria-label={minLabel || `Minimum ${String(label).toLowerCase()}`}
            aria-valuetext={spoken(lo)}
            onChange={e => setMin(Number(e.target.value))}
          />
        )}
        <input
          type="range"
          id={`${id}-max`}
          className="range-dual z-[1]"
          min={min}
          max={max}
          step={step}
          value={hi}
          aria-label={
            dual
              ? maxLabel || `Maximum ${String(label).toLowerCase()}`
              : undefined
          }
          aria-valuetext={spoken(hi)}
          onChange={e => setMax(Number(e.target.value))}
        />
      </div>
    </div>
  )
}

RangeSlider.propTypes = {
  label: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.shape({
      min: PropTypes.number.isRequired,
      max: PropTypes.number.isRequired,
    }),
  ]).isRequired,
  onChange: PropTypes.func.isRequired,
  format: PropTypes.func,
  unit: PropTypes.string,
  openTop: PropTypes.bool,
  minLabel: PropTypes.string,
  maxLabel: PropTypes.string,
  className: PropTypes.string,
  // The label's look; the housemate filters set it as a small-caps label
  // to match the section labels beside it.
  labelClassName: PropTypes.string,
  id: PropTypes.string,
}
